import { useTheme } from "@mui/material";
import { type EChartsOption } from "echarts";
import ECharts from "echarts-for-react";

import ChartCard from "../ClusterDetail/Charts/ChartCard";
import { formatMoney, getCommonChartOptions } from "../ClusterDetail/Charts/utils";

interface ClusterCostItem {
  name: string;
  value: number;
  date: string;
}

const clusterCostData: ClusterCostItem[] = [
  { name: "Production Cluster", value: 5000, date: "2023-10-01" },
  { name: "Staging Cluster", value: 950, date: "2023-10-01" },
  { name: "Development Cluster", value: 780, date: "2023-10-01" },
  { name: "Analytics Cluster", value: 1100, date: "2023-10-01" },
  { name: "Testing Cluster", value: 670, date: "2023-10-01" },
  { name: "Backup Cluster", value: 890, date: "2023-10-01" },
  { name: "Monitoring Cluster", value: 1020, date: "2023-10-01" },
  { name: "Edge Cluster", value: 560, date: "2023-10-01" },
  { name: "AI/ML Cluster", value: 750, date: "2023-10-01" },
  { name: "Database Cluster", value: 720, date: "2023-10-01" },

  { name: "Production Cluster", value: 5200, date: "2023-10-02" },
  { name: "Staging Cluster", value: 970, date: "2023-10-02" },
  { name: "Development Cluster", value: 800, date: "2023-10-02" },
  { name: "Analytics Cluster", value: 1120, date: "2023-10-02" },
  { name: "Testing Cluster", value: 690, date: "2023-10-02" },
  { name: "Backup Cluster", value: 910, date: "2023-10-02" },
  { name: "Monitoring Cluster", value: 1040, date: "2023-10-02" },
  { name: "Edge Cluster", value: 580, date: "2023-10-02" },
  { name: "AI/ML Cluster", value: 7200, date: "2023-10-02" },
  { name: "Database Cluster", value: 740, date: "2023-10-02" },

  { name: "Production Cluster", value: 5400, date: "2023-10-03" },
  { name: "Staging Cluster", value: 990, date: "2023-10-03" },
  { name: "Development Cluster", value: 820, date: "2023-10-03" },
  { name: "Analytics Cluster", value: 1140, date: "2023-10-03" },
  { name: "Testing Cluster", value: 710, date: "2023-10-03" },
  { name: "Backup Cluster", value: 930, date: "2023-10-03" },
  { name: "Monitoring Cluster", value: 1060, date: "2023-10-03" },
  { name: "Edge Cluster", value: 600, date: "2023-10-03" },
  { name: "AI/ML Cluster", value: 4000, date: "2023-10-03" },
  { name: "Database Cluster", value: 760, date: "2023-10-03" },

  { name: "Production Cluster", value: 5600, date: "2023-10-04" },
  { name: "Staging Cluster", value: 1010, date: "2023-10-04" },
  { name: "Development Cluster", value: 840, date: "2023-10-04" },
  { name: "Analytics Cluster", value: 1160, date: "2023-10-04" },
  { name: "Testing Cluster", value: 730, date: "2023-10-04" },
  { name: "Backup Cluster", value: 950, date: "2023-10-04" },
  { name: "Monitoring Cluster", value: 1080, date: "2023-10-04" },
  { name: "Edge Cluster", value: 620, date: "2023-10-04" },
  { name: "AI/ML Cluster", value: 7600, date: "2023-10-04" },
  { name: "Database Cluster", value: 780, date: "2023-10-04" },

  { name: "Production Cluster", value: 5800, date: "2023-10-05" },
  { name: "Staging Cluster", value: 1030, date: "2023-10-05" },
  { name: "Development Cluster", value: 860, date: "2023-10-05" },
  { name: "Analytics Cluster", value: 1180, date: "2023-10-05" },
  { name: "Testing Cluster", value: 750, date: "2023-10-05" },
  { name: "Backup Cluster", value: 970, date: "2023-10-05" },
  { name: "Monitoring Cluster", value: 1100, date: "2023-10-05" },
  { name: "Edge Cluster", value: 640, date: "2023-10-05" },
  { name: "AI/ML Cluster", value: 7800, date: "2023-10-05" },
  { name: "Database Cluster", value: 800, date: "2023-10-05" },

  { name: "Production Cluster", value: 6000, date: "2023-10-06" },
  { name: "Staging Cluster", value: 1050, date: "2023-10-06" },
  { name: "Development Cluster", value: 880, date: "2023-10-06" },
  { name: "Analytics Cluster", value: 1200, date: "2023-10-06" },
  { name: "Testing Cluster", value: 770, date: "2023-10-06" },
  { name: "Backup Cluster", value: 990, date: "2023-10-06" },
  { name: "Monitoring Cluster", value: 1120, date: "2023-10-06" },
  { name: "Edge Cluster", value: 660, date: "2023-10-06" },
  { name: "AI/ML Cluster", value: 10000, date: "2023-10-06" },
  { name: "Database Cluster", value: 820, date: "2023-10-06" },
];

const ClusterCostChart = () => {
  const theme = useTheme();

  const sortedClusterNames = Array.from(new Set(clusterCostData.map((i) => i.name))).sort((a, b) => {
    const calculateTotal = (name: string) =>
      clusterCostData.filter((i) => i.name === name).reduce((sum, item) => sum + item.value, 0);

    return calculateTotal(a) - calculateTotal(b);
  });

  const chartOption: EChartsOption = {
    ...getCommonChartOptions(theme, {
      grid: {
        top: 10,
        left: 50,
        right: 10,
        bottom: 50,
      },
      tooltip: {
        trigger: "item",
        valueFormatter: (v) => formatMoney(v?.toString() ?? ""),
      },
    }),
    xAxis: {
      type: "category",
      data: Array.from(new Set(clusterCostData.map((i) => i.date))),
    },
    yAxis: {
      type: "value",
      axisLabel: {
        formatter: (value) => formatMoney(value.toString()),
      },
    },

    series: sortedClusterNames.map((name) => ({
      name,
      type: "bar",
      stack: "total",
      barWidth: "40%",
      emphasis: {
        focus: "series",
      },
      data: Array.from(new Set(clusterCostData.map((i) => i.date))).map(
        (date) => clusterCostData.find((i) => i.name === name && i.date === date)?.value || 0
      ),
    })),
  };

  return (
    <ChartCard title="Top 10 clusters by cost" height={310}>
      <ECharts
        option={chartOption}
        lazyUpdate
        style={{
          height: "100%",
          width: "100%",
        }}
      />
    </ChartCard>
  );
};

export default ClusterCostChart;
